import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import Modal from "./../../../../hooks/Modal";
import { getAllAssociations } from "./../../../../services/associationsServices";
import { TopVisitsBarChart } from "./Charts/TopVisitsBarChart";
import { MembersDonut } from "./Charts/MembersDonut";
import { MembersStackedBars } from "./Charts/MembersStackedBars";
import DOMPurify from "dompurify";
import {
  FiBell,
  FiMail,
  FiRefreshCw,
  FiVolume2,
  FiVolumeX,
  FiDownload,
} from "react-icons/fi";

/* =========================
   Const / helpers
========================= */

const GRADIENT =
  "bg-gradient-to-r from-[#5A8DEE] via-[#8753FF] to-[#FF6A88] text-white";

const todayISO = () => new Date().toISOString().split("T")[0];

const DEFAULT_PATHS = {
  visits: "/api/visites/countVisitesByDate",
  totalUsers: "/api/users/countUsers",
  todayUsers: "/api/users/countUsersToday",
  nombreAdherents: "/api/dashboard/comptageStatutLicencie",
  nombreInscrits: "/api/dashboard/comptageInscriptionsTotales",
  notifications: "/api/notifications/1",
  messages: "/api/messages/allmessages/1",
};

const cn = (...a) => a.filter(Boolean).join(" ");

const fmtInt = (n) =>
  new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(
    Number(n || 0),
  );

function useDebouncedValue(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const pct = (a, b) => {
  const A = Number(a || 0);
  const B = Number(b || 0);
  if (!B) return 0;
  return (A / B) * 100;
};

const median = (values = []) => {
  const arr = values.map((x) => Number(x || 0)).sort((a, b) => a - b);
  if (!arr.length) return 0;
  const mid = Math.floor(arr.length / 2);
  return arr.length % 2 ? arr[mid] : (arr[mid - 1] + arr[mid]) / 2;
};

/* =========================
   Dashboard
========================= */

export default function ClubSummaryDashboard() {
  const [clubData, setClubData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Modales
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpenMessage, setIsModalOpenMessage] = useState(false);

  // Filtres
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 250);
  const [minLicencies, setMinLicencies] = useState(0);
  const [onlyWithNotif, setOnlyWithNotif] = useState(false);
  const [onlyWithUnreadMsg, setOnlyWithUnreadMsg] = useState(false);
  const [sortKey, setSortKey] = useState("visits"); // visits | totalUsers | nombreAdherents | nombreInscrits | name
  const [sortDir, setSortDir] = useState("desc"); // asc | desc
  const [dateStr, setDateStr] = useState(todayISO());
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Graph UI
  const [topMetric, setTopMetric] = useState("visits"); // visits | totalUsers | nombreAdherents | nombreInscrits

  // Progress / meta
  const abortRef = useRef(null);
  const intervalRef = useRef(null);

  // Flash nouveaux messages / notifs
  const [flashMsgMap, setFlashMsgMap] = useState({}); // { clubId: true }
  const [flashNotifMap, setFlashNotifMap] = useState({}); // { clubId: true }

  const lastMsgAtRef = useRef({}); // { clubId: timestamp }
  const lastUnreadCountRef = useRef({}); // { clubId: number }
  const lastNotifAtRef = useRef({}); // { clubId: timestamp }
  const lastNotifCountRef = useRef({}); // { clubId: number }

  // Deltas (nouveaux users/licenciés/inscrits)
  const [deltaMap, setDeltaMap] = useState({}); // { clubId: { users:+x, lic:+x, ins:+x } }
  const lastCountsRef = useRef({}); // { clubId: { totalUsers, nombreAdherents, nombreInscrits } }

  // Son
  const [soundOn, setSoundOn] = useState(() => {
    try {
      return localStorage.getItem("dashboard_sound") === "on";
    } catch {
      return false;
    }
  });
  const audioCtxRef = useRef(null);

  const playBeep = useCallback(async () => {
    if (!soundOn) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!audioCtxRef.current) audioCtxRef.current = new AC();
      const ctx = audioCtxRef.current;
      await ctx.resume?.();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 880;
      gain.gain.value = 0.02;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.18);
    } catch {}
  }, [soundOn]);

  const toggleSound = useCallback(async () => {
    const next = !soundOn;
    setSoundOn(next);
    try {
      localStorage.setItem("dashboard_sound", next ? "on" : "off");
    } catch {}
    if (next) playBeep();
  }, [soundOn, playBeep]);

  const formatDateTime = (dateTime) =>
    new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(dateTime));

  const handleViewNotifications = (notifications) => {
    setSelectedNotifications(notifications);
    setIsModalOpen(true);
  };

  const handleViewMessages = (messages) => {
    setSelectedMessages(messages);
    setIsModalOpenMessage(true);
  };

  const fetchAllClubsData = useCallback(async () => {
    setIsRefreshing(true);
    abortRef.current?.abort?.();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    if (!clubData.length) setLoading(true);

    let allAssociations = [];
    try {
      allAssociations = (await getAllAssociations()) || [];
    } catch (e) {
      console.error("getAllAssociations error:", e);
      setIsRefreshing(false);
      setLoading(false);
      return;
    }

    const filtered = allAssociations.filter(
      (asso) =>
        asso.produit_asso === "MonAppliClub Standard" &&
        !!asso.apiUrl_asso &&
        /^https?:\/\//.test(asso.apiUrl_asso),
    );

    // petit throttle de concurrence
    const CONCURRENCY = 8;
    let i = 0;
    const results = [];

    const worker = async () => {
      while (i < filtered.length) {
        const asso = filtered[i++];
        const res = await fetchClubDetails(
          {
            id: asso.id,
            name: asso.nom_asso,
            logo: asso.logo_asso,
            appUrl: asso.siteweb_asso,
            apiUrl: asso.apiUrl_asso,
            dataPaths: { ...DEFAULT_PATHS },
          },
          dateStr,
          signal,
        );
        results.push(res);
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, filtered.length) }, () =>
        worker(),
      ),
    );

    const ok = results;

    // --- Flash messages / notifs + deltas stats ---
    const newMsgFlash = {};
    const newNotifFlash = {};
    const newDeltas = {};

    ok.forEach((c) => {
      const id = c.id;

      // messages unread
      const latestMsgTs = (c.messages || []).reduce((acc, m) => {
        const t = new Date(m.created_at).getTime();
        return Number.isFinite(t) ? Math.max(acc, t) : acc;
      }, 0);
      const unreadCount = (c.messages || []).length;

      const prevMsgTs = lastMsgAtRef.current[id] || 0;
      const prevUnread = lastUnreadCountRef.current[id] ?? 0;

      const hasNewMsg =
        (latestMsgTs && prevMsgTs && latestMsgTs > prevMsgTs) ||
        (prevUnread && unreadCount > prevUnread);

      if (hasNewMsg) newMsgFlash[id] = true;

      if (latestMsgTs) lastMsgAtRef.current[id] = latestMsgTs;
      lastUnreadCountRef.current[id] = unreadCount;

      // notifications (du jour filtré)
      const latestNotifTs = (c.notifications || []).reduce((acc, n) => {
        const t = new Date(n.created_at).getTime();
        return Number.isFinite(t) ? Math.max(acc, t) : acc;
      }, 0);
      const notifCount = (c.notifications || []).length;

      const prevNotifTs = lastNotifAtRef.current[id] || 0;
      const prevNotifCount = lastNotifCountRef.current[id] ?? 0;

      const hasNewNotif =
        (latestNotifTs && prevNotifTs && latestNotifTs > prevNotifTs) ||
        (prevNotifCount && notifCount > prevNotifCount);

      if (hasNewNotif) newNotifFlash[id] = true;

      if (latestNotifTs) lastNotifAtRef.current[id] = latestNotifTs;
      lastNotifCountRef.current[id] = notifCount;

      // deltas stats (users/lic/ins)
      const prevCounts = lastCountsRef.current[id];
      const nowCounts = {
        totalUsers: Number(c.totalUsers || 0),
        nombreAdherents: Number(c.nombreAdherents || 0),
        nombreInscrits: Number(c.nombreInscrits || 0),
      };

      if (prevCounts) {
        const du = nowCounts.totalUsers - Number(prevCounts.totalUsers || 0);
        const dl =
          nowCounts.nombreAdherents - Number(prevCounts.nombreAdherents || 0);
        const di =
          nowCounts.nombreInscrits - Number(prevCounts.nombreInscrits || 0);
        if (du > 0 || dl > 0 || di > 0) {
          newDeltas[id] = {
            users: Math.max(0, du),
            lic: Math.max(0, dl),
            ins: Math.max(0, di),
          };
        }
      }
      lastCountsRef.current[id] = nowCounts;
    });

    if (Object.keys(newMsgFlash).length) {
      setFlashMsgMap((p) => ({ ...p, ...newMsgFlash }));
      playBeep();
      setTimeout(() => {
        setFlashMsgMap((p) => {
          const copy = { ...p };
          Object.keys(newMsgFlash).forEach((k) => delete copy[k]);
          return copy;
        });
      }, 4500);
    }

    if (Object.keys(newNotifFlash).length) {
      setFlashNotifMap((p) => ({ ...p, ...newNotifFlash }));
      setTimeout(() => {
        setFlashNotifMap((p) => {
          const copy = { ...p };
          Object.keys(newNotifFlash).forEach((k) => delete copy[k]);
          return copy;
        });
      }, 4500);
    }

    if (Object.keys(newDeltas).length) {
      setDeltaMap((p) => ({ ...p, ...newDeltas }));
      setTimeout(() => {
        setDeltaMap((p) => {
          const copy = { ...p };
          Object.keys(newDeltas).forEach((k) => delete copy[k]);
          return copy;
        });
      }, 6500);
    }

    setClubData(ok);
    setIsRefreshing(false);
    setLoading(false);
  }, [clubData.length, dateStr, playBeep]);

  useEffect(() => {
    fetchAllClubsData();
    return () => abortRef.current?.abort?.();
  }, [fetchAllClubsData]);

  useEffect(() => {
    clearInterval(intervalRef.current);
    if (!autoRefresh) return;
    intervalRef.current = setInterval(fetchAllClubsData, 5 * 60 * 1000);
    return () => clearInterval(intervalRef.current);
  }, [autoRefresh, fetchAllClubsData]);

  // --------- filtres + tri ---------
  const filteredSorted = useMemo(() => {
    const s = debouncedSearch.trim().toLowerCase();
    let arr = clubData.filter((c) => {
      const matchesSearch =
        !s ||
        (c.name || "").toLowerCase().includes(s) ||
        (c.appUrl || "").toLowerCase().includes(s);

      const matchesLic =
        (c.nombreAdherents || 0) >= (Number(minLicencies) || 0);
      const hasNotif = (c.notifications?.length || 0) > 0;
      const matchesNotif = !onlyWithNotif || hasNotif;

      const unreadCount = (c.messages || []).length;
      const matchesUnread = !onlyWithUnreadMsg || unreadCount > 0;

      return matchesSearch && matchesLic && matchesNotif && matchesUnread;
    });

    const dir = sortDir === "asc" ? 1 : -1;

    arr.sort((a, b) => {
      if (sortKey === "name")
        return (a.name || "").localeCompare(b.name || "") * dir;
      const va = Number(a[sortKey] || 0);
      const vb = Number(b[sortKey] || 0);
      if (va === vb) return (a.name || "").localeCompare(b.name || "");
      return (va < vb ? -1 : 1) * dir;
    });

    return arr;
  }, [
    clubData,
    debouncedSearch,
    minLicencies,
    onlyWithNotif,
    onlyWithUnreadMsg,
    sortKey,
    sortDir,
  ]);

  // Totaux
  const totals = useMemo(() => {
    return filteredSorted.reduce(
      (acc, c) => {
        acc.visits += c.visits || 0;
        acc.totalUsers += c.totalUsers || 0;
        acc.todayUsers += c.todayUsers || 0;
        acc.licencies += c.nombreAdherents || 0;
        acc.inscrits += c.nombreInscrits || 0;
        acc.notifications += c.notifications?.length || 0;
        acc.messages += (c.messages || []).length || 0;
        acc.errors += c.error ? 1 : 0;
        return acc;
      },
      {
        visits: 0,
        totalUsers: 0,
        todayUsers: 0,
        licencies: 0,
        inscrits: 0,
        notifications: 0,
        messages: 0,
        errors: 0,
      },
    );
  }, [filteredSorted]);

  // Top metric data (chart)
  const topMetricData = useMemo(() => {
    const labelMap = {
      visits: "Visites",
      totalUsers: "Utilisateurs",
      nombreAdherents: "Licenciés",
      nombreInscrits: "Inscrits",
    };
    const key = topMetric;
    return [...filteredSorted]
      .sort((a, b) => Number(b[key] || 0) - Number(a[key] || 0))
      .slice(0, 10)
      .map((c) => ({
        name: c.name,
        value: Number(c[key] || 0),
        label: labelMap[key],
      }));
  }, [filteredSorted, topMetric]);

  const membersStackData = useMemo(() => {
    return [...filteredSorted]
      .map((c) => ({
        name: c.name,
        Licenciés: c.nombreAdherents || 0,
        Inscrits: c.nombreInscrits || 0,
        total: (c.nombreAdherents || 0) + (c.nombreInscrits || 0),
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [filteredSorted]);

  // ===== Résumés utiles pour les graphiques (infos pertinentes) =====

  const topMetricSummary = useMemo(() => {
    const key = topMetric;
    const total = filteredSorted.reduce(
      (acc, c) => acc + Number(c[key] || 0),
      0,
    );
    const values = filteredSorted.map((c) => Number(c[key] || 0));
    const avg = filteredSorted.length ? total / filteredSorted.length : 0;
    const med = median(values);

    const top1 = topMetricData?.[0];
    const top10Sum = (topMetricData || []).reduce(
      (acc, x) => acc + Number(x.value || 0),
      0,
    );

    return {
      key,
      total,
      avg,
      med,
      top1Name: top1?.name || "—",
      top1Value: Number(top1?.value || 0),
      top10Sum,
      top10Pct: pct(top10Sum, total),
    };
  }, [filteredSorted, topMetric, topMetricData]);

  const donutSummary = useMemo(() => {
    const lic = Number(totals.licencies || 0);
    const ins = Number(totals.inscrits || 0);
    const total = lic + ins;
    return {
      lic,
      ins,
      total,
      insPct: pct(ins, total),
      licPct: pct(lic, total),
    };
  }, [totals.licencies, totals.inscrits]);

  const stackedSummary = useMemo(() => {
    const top = membersStackData?.[0];
    const topTotal = Number(top?.total || 0);
    const topName = top?.name || "—";
    const sumTop10 = (membersStackData || []).reduce(
      (acc, x) => acc + Number(x.total || 0),
      0,
    );

    const globalTotal = filteredSorted.reduce(
      (acc, c) =>
        acc + Number(c.nombreAdherents || 0) + Number(c.nombreInscrits || 0),
      0,
    );

    return {
      topName,
      topTotal,
      sumTop10,
      sumTop10Pct: pct(sumTop10, globalTotal),
    };
  }, [membersStackData, filteredSorted]);

  const exportCSV = () => {
    const headers = [
      "Club",
      "Visites du jour",
      "Utilisateurs",
      "Utilisateurs (aujourd'hui)",
      "Licenciés",
      "Inscrits",
      "Notifications (jour)",
      "Messages non lus",
      "URL",
    ];
    const rows = filteredSorted.map((c) => [
      c.name,
      c.visits || 0,
      c.totalUsers || 0,
      c.todayUsers || 0,
      c.nombreAdherents || 0,
      c.nombreInscrits || 0,
      c.notifications?.length || 0,
      (c.messages || []).length || 0,
      c.appUrl || "",
    ]);
    const csv =
      headers.join(";") +
      "\n" +
      rows
        .map((r) =>
          r.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(";"),
        )
        .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `clubs_${dateStr}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleSort = (key) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDir("desc");
      return key;
    });
  };

  const metricTabs = [
    { key: "visits", label: "Visites" },
    { key: "totalUsers", label: "Utilisateurs" },
    { key: "nombreAdherents", label: "Licenciés" },
    { key: "nombreInscrits", label: "Inscrits" },
  ];

  return (
    <div className="space-y-6">
      <style>{`
@keyframes softBlink {
  0%   { background-color: transparent; }
  50%  { background-color: rgba(251, 191, 36, 0.22); }
  100% { background-color: transparent; }
}
.soft-blink { animation: softBlink 1.5s ease-in-out 3; }

@keyframes iconPulse {
  0%   { box-shadow: 0 0 0 0 rgba(59,130,246,0.55); }
  70%  { box-shadow: 0 0 0 10px rgba(59,130,246,0); }
  100% { box-shadow: 0 0 0 0 rgba(59,130,246,0); }
}
.icon-pulse { animation: iconPulse 1.4s ease-out infinite; }

.table-sticky thead th {
  position: sticky;
  top: 0;
  z-index: 5;
  backdrop-filter: blur(8px);
}
      `}</style>

      {/* HEADER */}
      <div className={cn("rounded-2xl p-5 shadow-sm", GRADIENT)}>
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold">
              Statistiques des Clubs : MonAppliClub
            </h2>

            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs opacity-95">
              <span
                className={cn(
                  "inline-flex items-center gap-2 px-2 py-1 rounded-full bg-white/15",
                )}
              >
                <span
                  className={cn(
                    "h-2 w-2 rounded-full",
                    isRefreshing ? "bg-amber-300" : "bg-emerald-300",
                  )}
                />
                {isRefreshing ? "Rafraîchissement…" : "À jour"}
              </span>

              <span className="px-2 py-1 rounded-full bg-white/15">
                Clubs : {fmtInt(filteredSorted.length)}
              </span>

              {totals.errors > 0 ? (
                <span className="px-2 py-1 rounded-full bg-black/25">
                  Erreurs : {fmtInt(totals.errors)}
                </span>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={fetchAllClubsData}
              disabled={isRefreshing}
              className={cn(
                "px-3 py-2 rounded-xl text-sm bg-white/15 hover:bg-white/25 transition inline-flex items-center gap-2",
                isRefreshing && "opacity-70 cursor-not-allowed",
              )}
              title="Rafraîchir maintenant"
            >
              <FiRefreshCw className={cn(isRefreshing && "animate-spin")} />
              Rafraîchir
            </button>

            <button
              onClick={() => setAutoRefresh((v) => !v)}
              className={cn(
                "px-3 py-2 rounded-xl text-sm transition",
                autoRefresh
                  ? "bg-white/15 hover:bg-white/25"
                  : "bg-black/20 hover:bg-black/30",
              )}
              title="Auto-refresh toutes les 5 minutes"
            >
              Auto-refresh : {autoRefresh ? "ON" : "OFF"}
            </button>

            <button
              onClick={exportCSV}
              className="px-3 py-2 rounded-xl bg-white/15 hover:bg-white/25 transition text-sm inline-flex items-center gap-2"
              title="Exporter CSV"
            >
              <FiDownload />
              Export CSV
            </button>

            <button
              onClick={toggleSound}
              className="px-3 py-2 rounded-xl bg-white/15 hover:bg-white/25 transition text-sm inline-flex items-center gap-2"
              title="Son nouveaux messages"
            >
              {soundOn ? <FiVolume2 /> : <FiVolumeX />}
              Son : {soundOn ? "ON" : "OFF"}
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mt-5">
          <TotalPill label="Visites" value={fmtInt(totals.visits)} />
          <TotalPill
            label="Utilisateurs"
            value={fmtInt(totals.totalUsers)}
            subtitle={
              totals.todayUsers
                ? `+${fmtInt(totals.todayUsers)} aujourd’hui`
                : ""
            }
          />
          <TotalPill label="Licenciés" value={fmtInt(totals.licencies)} />
          <TotalPill label="Inscrits" value={fmtInt(totals.inscrits)} />
          <TotalPill
            label="Notifications"
            value={fmtInt(totals.notifications)}
          />
          <TotalPill
            label="Messages non lus"
            value={fmtInt(totals.messages)}
            pulse={totals.messages > 0}
          />
          <div className="flex items-center justify-end">
            <input
              type="date"
              className="text-slate-900 rounded-lg px-3 py-1.5 w-full bg-white"
              value={dateStr}
              onChange={(e) => setDateStr(e.target.value)}
              title="Jour de référence (visites + notifications)"
            />
          </div>
        </div>
      </div>

      {/* FILTRES */}
      <div className="rounded-2xl p-4 bg-white dark:bg-slate-900 shadow-sm border border-slate-200 dark:border-slate-800">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-2">
            <label className="text-xs text-slate-500">Recherche</label>
            <input
              placeholder="Rechercher un club…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
            />
          </div>

          <div>
            <label className="text-xs text-slate-500">Min licenciés</label>
            <input
              type="number"
              min={0}
              value={minLicencies}
              onChange={(e) => setMinLicencies(e.target.value)}
              className="mt-1 px-3 py-2 rounded-lg w-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-xs text-slate-500">Tri</label>
            <div className="mt-1 flex items-center gap-2">
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
                className="px-3 py-2 rounded-lg w-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
              >
                <option value="visits">Visites</option>
                <option value="totalUsers">Utilisateurs</option>
                <option value="nombreAdherents">Licenciés</option>
                <option value="nombreInscrits">Inscrits</option>
                <option value="name">Nom</option>
              </select>
              <button
                onClick={() =>
                  setSortDir((d) => (d === "asc" ? "desc" : "asc"))
                }
                className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/60"
                title="Inverser l’ordre"
              >
                {sortDir === "asc" ? "↑" : "↓"}
              </button>
            </div>
          </div>

          <div className="flex items-end gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={onlyWithNotif}
                onChange={(e) => setOnlyWithNotif(e.target.checked)}
              />
              Avec notifs
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={onlyWithUnreadMsg}
                onChange={(e) => setOnlyWithUnreadMsg(e.target.checked)}
              />
              Avec messages
            </label>
          </div>
        </div>
      </div>

      {/* GRAPHIQUES */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartCard
          title="Top 10"
          subtitle="Choisis la métrique"
          right={
            <div className="flex flex-wrap gap-1">
              {metricTabs.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTopMetric(t.key)}
                  className={cn(
                    "px-2 py-1 rounded-lg text-xs border transition",
                    topMetric === t.key
                      ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900 dark:border-white"
                      : "border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/60",
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
          }
          footer={
            <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 dark:text-slate-300">
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800/40 p-2">
                <div className="opacity-70">Total</div>
                <div className="font-semibold text-slate-900 dark:text-slate-100">
                  {fmtInt(topMetricSummary.total)}
                </div>
              </div>
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800/40 p-2">
                <div className="opacity-70">Top 10 = part</div>
                <div className="font-semibold text-slate-900 dark:text-slate-100">
                  {topMetricSummary.top10Pct.toFixed(1)}%
                </div>
              </div>
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800/40 p-2">
                <div className="opacity-70">Moyenne / Médiane</div>
                <div className="font-semibold text-slate-900 dark:text-slate-100">
                  {fmtInt(topMetricSummary.avg)} /{" "}
                  {fmtInt(topMetricSummary.med)}
                </div>
              </div>
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800/40 p-2">
                <div className="opacity-70">#1</div>
                <div
                  className="font-semibold text-slate-900 dark:text-slate-100 truncate"
                  title={topMetricSummary.top1Name}
                >
                  {topMetricSummary.top1Name} (
                  {fmtInt(topMetricSummary.top1Value)})
                </div>
              </div>
            </div>
          }
        >
          <TopVisitsBarChart
            data={topMetricData}
            loading={loading && !clubData.length}
            valueLabel={topMetricData?.[0]?.label || ""}
            total={topMetricSummary.total}
          />
        </ChartCard>

        <ChartCard
          title="Répartition"
          subtitle="Licenciés vs Inscrits"
          footer={
            <div className="grid grid-cols-3 gap-2 text-xs text-slate-600 dark:text-slate-300">
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800/40 p-2">
                <div className="opacity-70">Total</div>
                <div className="font-semibold text-slate-900 dark:text-slate-100">
                  {fmtInt(donutSummary.total)}
                </div>
              </div>
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800/40 p-2">
                <div className="opacity-70">Inscrits</div>
                <div className="font-semibold text-slate-900 dark:text-slate-100">
                  {fmtInt(donutSummary.ins)} ({donutSummary.insPct.toFixed(1)}%)
                </div>
              </div>
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800/40 p-2">
                <div className="opacity-70">Licenciés</div>
                <div className="font-semibold text-slate-900 dark:text-slate-100">
                  {fmtInt(donutSummary.lic)} ({donutSummary.licPct.toFixed(1)}%)
                </div>
              </div>
            </div>
          }
        >
          <MembersDonut
            licencies={totals.licencies}
            inscrits={totals.inscrits}
            loading={loading && !clubData.length}
          />
        </ChartCard>

        <ChartCard
          title="Top clubs"
          subtitle="Licenciés + Inscrits (10 max)"
          footer={
            <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 dark:text-slate-300">
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800/40 p-2">
                <div className="opacity-70">#1</div>
                <div
                  className="font-semibold text-slate-900 dark:text-slate-100 truncate"
                  title={stackedSummary.topName}
                >
                  {stackedSummary.topName} ({fmtInt(stackedSummary.topTotal)})
                </div>
              </div>
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800/40 p-2">
                <div className="opacity-70">Top 10 = part</div>
                <div className="font-semibold text-slate-900 dark:text-slate-100">
                  {stackedSummary.sumTop10Pct.toFixed(1)}%
                </div>
              </div>
            </div>
          }
        >
          <MembersStackedBars
            data={membersStackData}
            loading={loading && !clubData.length}
          />
        </ChartCard>
      </div>

      {/* TABLE */}
      <div className="rounded-2xl overflow-hidden bg-white dark:bg-slate-900 shadow-sm border border-slate-200 dark:border-slate-800">
        {loading && !clubData.length ? (
          <SkeletonList />
        ) : (
          <ClubsTable
            rows={filteredSorted}
            flashMsgMap={flashMsgMap}
            flashNotifMap={flashNotifMap}
            deltaMap={deltaMap}
            onViewNotif={handleViewNotifications}
            onViewMsgs={handleViewMessages}
            sortKey={sortKey}
            sortDir={sortDir}
            onToggleSort={toggleSort}
          />
        )}
      </div>

      {/* MODALES */}
      {isModalOpen && (
        <Modal
          onClose={() => setIsModalOpen(false)}
          title={`Notifications du ${dateStr}`}
        >
          <NotificationsModalContent
            items={selectedNotifications}
            formatDateTime={formatDateTime}
            dateStr={dateStr}
          />
        </Modal>
      )}

      {isModalOpenMessage && (
        <Modal
          onClose={() => setIsModalOpenMessage(false)}
          title={`Messages non lus`}
        >
          <MessagesModalContent
            items={selectedMessages.filter((m) => !m.is_copy)}
            formatDateTime={formatDateTime}
            dateStr={dateStr}
          />
        </Modal>
      )}
    </div>
  );
}

/* ===================================================================================
   UI
=================================================================================== */

function ChartCard({ title, subtitle, right, footer, children }) {
  return (
    <div className="rounded-2xl bg-white dark:bg-slate-900 p-4 shadow-sm border border-slate-200 dark:border-slate-800">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-semibold leading-tight">{title}</h3>
          {subtitle ? (
            <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>
          ) : null}
        </div>
        {right ? <div className="flex-shrink-0">{right}</div> : null}
      </div>

      {children}

      {footer ? (
        <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
          {footer}
        </div>
      ) : null}
    </div>
  );
}

function TotalPill({ label, value, subtitle, pulse = false }) {
  return (
    <div
      className={cn(
        "rounded-xl p-3 ring-1 ring-white/10",
        pulse ? "bg-blue-500/20 ring-blue-400/30" : "bg-white/10",
      )}
    >
      <div className="text-xs opacity-90">{label}</div>
      <div className="text-xl font-semibold leading-tight tabular-nums">
        {value}
      </div>
      {subtitle ? (
        <div className="text-[11px] opacity-80 mt-0.5">{subtitle}</div>
      ) : null}
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="p-4 space-y-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-14 rounded-lg bg-slate-100 dark:bg-slate-800 animate-pulse"
        />
      ))}
    </div>
  );
}

function SortTh({ label, active, dir, onClick, align = "left" }) {
  return (
    <th className={cn("py-3 px-4", align === "right" && "text-right")}>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "inline-flex items-center gap-1 text-sm font-medium hover:opacity-90",
          active
            ? "text-slate-900 dark:text-slate-100"
            : "text-slate-600 dark:text-slate-300",
        )}
      >
        {label}
        {active ? (
          <span className="text-xs opacity-70">
            {dir === "asc" ? "↑" : "↓"}
          </span>
        ) : null}
      </button>
    </th>
  );
}

function DeltaBadge({ n }) {
  if (!n || n <= 0) return null;
  return (
    <span className="ml-2 inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200 px-2 py-0.5 text-[11px] font-semibold">
      +{fmtInt(n)}
    </span>
  );
}

function IconBadgeButton({
  title,
  count,
  onClick,
  pulse = false,
  tone = "blue",
  children,
}) {
  const tones = {
    blue: "bg-blue-300 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/35 dark:text-blue-200 dark:hover:bg-blue-900/55",
    amber:
      "bg-red-300 text-red-700 hover:bg-red-100 dark:bg-red-900/35 dark:text-red-200 dark:hover:bg-red-900/55",
    slate:
      "bg-slate-50 text-slate-700 hover:bg-slate-100 dark:bg-slate-800/60 dark:text-slate-200 dark:hover:bg-slate-800",
  };

  const disabled = !count;

  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "relative inline-flex items-center justify-center rounded-xl h-10 w-10 border border-slate-200 dark:border-slate-700 transition",
        disabled ? "opacity-45 cursor-not-allowed" : tones[tone] || tones.blue,
        pulse && !disabled ? "icon-pulse" : "",
      )}
    >
      {children}
      <span
        className={cn(
          "absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-bold leading-[18px] text-center",
          disabled
            ? "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-200"
            : "bg-slate-900 text-white dark:bg-white dark:text-slate-900",
        )}
      >
        {fmtInt(count || 0)}
      </span>
    </button>
  );
}

function ClubsTable({
  rows,
  onViewNotif,
  onViewMsgs,
  flashMsgMap,
  flashNotifMap,
  deltaMap,
  sortKey,
  sortDir,
  onToggleSort,
}) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-[980px] w-full table-sticky">
        <thead>
          <tr className="bg-slate-50/80 dark:bg-slate-800/60 text-left">
            <th className="py-3 px-4">Clubs MonAppliClub</th>
            <SortTh
              label="Visites"
              active={sortKey === "visits"}
              dir={sortDir}
              onClick={() => onToggleSort("visits")}
              align="right"
            />
            <SortTh
              label="Utilisateurs"
              active={sortKey === "totalUsers"}
              dir={sortDir}
              onClick={() => onToggleSort("totalUsers")}
              align="right"
            />
            <SortTh
              label="Licenciés"
              active={sortKey === "nombreAdherents"}
              dir={sortDir}
              onClick={() => onToggleSort("nombreAdherents")}
              align="right"
            />
            <SortTh
              label="Inscrits"
              active={sortKey === "nombreInscrits"}
              dir={sortDir}
              onClick={() => onToggleSort("nombreInscrits")}
              align="right"
            />
          </tr>
        </thead>

        <tbody>
          {rows.map((c, idx) => {
            const d = deltaMap?.[c.id] || {};
            return (
              <tr
                key={`${c.id ?? c.name}-${idx}`}
                className={cn(
                  "border-b border-slate-100 dark:border-slate-800",
                  idx % 2
                    ? "bg-white/50 dark:bg-slate-900"
                    : "bg-white dark:bg-slate-950",
                  "hover:bg-slate-50/70 dark:hover:bg-slate-900/60 transition",
                )}
              >
                {/* CLUB cell */}
                <td className="py-3 px-4">
                  <div className="flex items-center gap-3">
                    {/* icons notifs/messages */}
                    <div className="flex items-center gap-2">
                      <IconBadgeButton
                        title="Notifications"
                        count={c.notifications?.length || 0}
                        onClick={() => onViewNotif(c.notifications || [])}
                        pulse={!!flashNotifMap?.[c.id]}
                        tone="amber"
                      >
                        <FiBell size={18} />
                      </IconBadgeButton>

                      <IconBadgeButton
                        title="Messages non lus"
                        count={(c.messages || []).length || 0}
                        onClick={() => onViewMsgs(c.messages || [])}
                        pulse={!!flashMsgMap?.[c.id]}
                        tone="blue"
                      >
                        <FiMail size={18} />
                      </IconBadgeButton>
                    </div>

                    {/* logo + name */}
                    <img
                      src={c.logo}
                      alt={c.name}
                      className="w-10 h-10 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                    />
                    <div className="min-w-0">
                      <a
                        href={c.appUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-blue-600 hover:underline truncate block"
                        title={c.name}
                      >
                        {c.name}
                      </a>
                      {c.error ? (
                        <div className="mt-1 text-xs text-red-600 dark:text-red-300">
                          Erreur lors de la récupération des données
                        </div>
                      ) : null}
                    </div>
                  </div>
                </td>

                <td className="py-3 px-4 text-right tabular-nums">
                  {fmtInt(c.visits || 0)}
                </td>

                <td className="py-3 px-4 text-right tabular-nums">
                  {fmtInt(c.totalUsers || 0)}
                  <DeltaBadge n={d.users} />
                  {c.todayUsers > 0 ? (
                    <span className="ml-2 inline-flex items-center rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-200 px-2 py-0.5 text-[11px] font-semibold">
                      +{fmtInt(c.todayUsers)} aujourd’hui
                    </span>
                  ) : null}
                </td>

                <td className="py-3 px-4 text-right tabular-nums">
                  {fmtInt(c.nombreAdherents || 0)}
                  <DeltaBadge n={d.lic} />
                </td>

                <td className="py-3 px-4 text-right tabular-nums">
                  {fmtInt(c.nombreInscrits || 0)}
                  <DeltaBadge n={d.ins} />
                </td>
              </tr>
            );
          })}

          {!rows.length && (
            <tr>
              <td className="py-10 px-4 text-center text-slate-500" colSpan={5}>
                Aucun club ne correspond à vos filtres.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/* ================= Fetch club =================== */

async function fetchClubDetails(club, dayISO, signal) {
  const clubDetails = {
    id: club.id,
    name: club.name,
    logo: club.logo,
    appUrl: club.appUrl,
  };

  const dataPaths = { ...club.dataPaths };
  if (club.id === 1) {
    dataPaths.notifications = "/api/notifications/50";
    dataPaths.messages = "/api/messages/allmessages/50";
  }

  try {
    for (const [key, path] of Object.entries(dataPaths)) {
      const url = `${club.apiUrl}${path}`;
      const response = await axios.get(url, { signal, timeout: 12000 });
      const data = response?.data;

      switch (key) {
        case "visits": {
          const todayCount = Array.isArray(data)
            ? data.find((v) => v.date_visite === dayISO)?.compteur || 0
            : 0;
          clubDetails[key] = todayCount;
          break;
        }
        case "notifications": {
          const list = Array.isArray(data) ? data : [];
          clubDetails[key] = list.filter((n) =>
            String(n.created_at || "").startsWith(dayISO),
          );
          break;
        }
        case "messages": {
          const all = Array.isArray(data) ? data : [];
          const unread = all
            .filter((m) => {
              const isUnread =
                m.read_message === false ||
                m.read_message === 0 ||
                m.read_message === "0";
              return !m.is_copy && isUnread;
            })
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          clubDetails[key] = unread;
          break;
        }
        case "nombreAdherents":
          clubDetails[key] = (data && data.nombreAdherents) || 0;
          break;
        case "nombreInscrits":
          clubDetails[key] = (data && data.nombreInscrits) || 0;
          break;
        default:
          clubDetails[key] = (data && (data.count ?? data.value)) || 0;
      }
    }
  } catch (err) {
    console.error(`Erreur pour ${club.name}:`, err?.message);
    clubDetails.error = "Erreur lors de la récupération des données";
  }

  return clubDetails;
}

/* ---------- Utils modales ---------- */
function timeAgo(date) {
  try {
    const d = new Date(date);
    const diff = (Date.now() - d.getTime()) / 1000;
    const rtf = new Intl.RelativeTimeFormat("fr", { numeric: "auto" });
    const map = [
      ["year", 60 * 60 * 24 * 365],
      ["month", 60 * 60 * 24 * 30],
      ["week", 60 * 60 * 24 * 7],
      ["day", 60 * 60 * 24],
      ["hour", 60 * 60],
      ["minute", 60],
      ["second", 1],
    ];
    for (const [unit, sec] of map) {
      const v = Math.floor(diff / sec);
      if (Math.abs(v) >= 1) return rtf.format(-v, unit);
    }
    return "à l’instant";
  } catch {
    return "";
  }
}

function exportItemsCSV(items, dateStr, type = "notifications") {
  const headers =
    type === "notifications" ? ["Message", "Date"] : ["Message (HTML)", "Date"];
  const rows = items.map((it) => [
    (it.message ?? it.content ?? "").replace(/\n/g, " ").replace(/"/g, '""'),
    it.created_at ?? "",
  ]);
  const csv =
    headers.join(";") +
    "\n" +
    rows.map((r) => r.map((x) => `"${String(x)}"`).join(";")).join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${type}_${dateStr}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ---------- Header d’outils (recherche/tri/export) commun ---------- */
function ModalToolbar({
  query,
  setQuery,
  sort,
  setSort,
  onExport,
  count,
  placeholder = "Rechercher…",
}) {
  return (
    <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
      <div className="flex-1">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
        />
      </div>
      <div className="flex items-center gap-2">
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
          title="Trier"
        >
          <option value="desc">Plus récents d’abord</option>
          <option value="asc">Plus anciens d’abord</option>
        </select>
        <button
          onClick={onExport}
          className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/60"
          title="Exporter CSV"
        >
          Export CSV
        </button>
        <span className="text-xs px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
          {count} élément{count > 1 ? "s" : ""}
        </span>
      </div>
    </div>
  );
}

/* ---------- Notifications ---------- */
export function NotificationsModalContent({
  items = [],
  formatDateTime,
  dateStr,
}) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("desc");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const arr = (items || []).filter(
      (n) => !q || (n.message || "").toLowerCase().includes(q),
    );
    arr.sort((a, b) =>
      sort === "asc"
        ? new Date(a.created_at) - new Date(b.created_at)
        : new Date(b.created_at) - new Date(a.created_at),
    );
    return arr;
  }, [items, query, sort]);

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text || "");
    } catch (e) {
      console.warn("Clipboard error:", e);
    }
  };

  return (
    <div>
      <ModalToolbar
        query={query}
        setQuery={setQuery}
        sort={sort}
        setSort={setSort}
        count={filtered.length}
        onExport={() => exportItemsCSV(filtered, dateStr, "notifications")}
        placeholder="Rechercher une notification…"
      />

      {filtered.length === 0 ? (
        <EmptyState
          label="Aucune notification"
          hint="Aucune notification trouvée pour ces critères."
        />
      ) : (
        <ul className="max-h-[70vh] overflow-auto pr-1 space-y-3">
          {filtered.map((n, i) => (
            <li
              key={`${n.created_at}-${i}`}
              className="p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-blue-700">
                    🔔
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-900 dark:text-slate-100 leading-relaxed">
                    {n.message}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800">
                      {formatDateTime(n.created_at)}
                    </span>
                    <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800">
                      {timeAgo(n.created_at)}
                    </span>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <button
                    onClick={() => copyText(n.message)}
                    className="px-2 py-1 text-xs rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/60"
                    title="Copier"
                  >
                    Copier
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ---------- Messages ---------- */
export function MessagesModalContent({ items = [], formatDateTime, dateStr }) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("desc");
  const [expanded, setExpanded] = useState({});

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const arr = (items || []).filter((m) => {
      const txt = String(m.content || "")
        .toLowerCase()
        .replace(/<[^>]+>/g, " ");
      return !q || txt.includes(q);
    });
    arr.sort((a, b) =>
      sort === "asc"
        ? new Date(a.created_at) - new Date(b.created_at)
        : new Date(b.created_at) - new Date(a.created_at),
    );
    return arr;
  }, [items, query, sort]);

  const copyHTML = async (html) => {
    try {
      await navigator.clipboard.writeText(html || "");
    } catch (e) {
      console.warn("Clipboard error:", e);
    }
  };

  return (
    <div>
      <ModalToolbar
        query={query}
        setQuery={setQuery}
        sort={sort}
        setSort={setSort}
        count={filtered.length}
        onExport={() => exportItemsCSV(filtered, dateStr, "messages")}
        placeholder="Rechercher dans les messages…"
      />

      {filtered.length === 0 ? (
        <EmptyState
          label="Aucun message"
          hint="Aucun message non lu pour ces critères."
        />
      ) : (
        <ul className="max-h-[70vh] overflow-auto pr-1 space-y-3">
          {filtered.map((m, i) => {
            const isOpen = !!expanded[i];
            const clean = DOMPurify.sanitize(m.content || "");
            return (
              <li
                key={`${m.created_at}-${i}`}
                className="p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                      ✉️
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className={
                        isOpen
                          ? "prose dark:prose-invert max-w-none"
                          : "line-clamp-3 text-sm"
                      }
                      dangerouslySetInnerHTML={{ __html: clean }}
                    />
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800">
                        {formatDateTime(m.created_at)}
                      </span>
                      <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800">
                        {timeAgo(m.created_at)}
                      </span>
                    </div>
                  </div>
                  <div className="flex-shrink-0 flex flex-col gap-2">
                    <button
                      onClick={() =>
                        setExpanded((prev) => ({ ...prev, [i]: !isOpen }))
                      }
                      className="px-2 py-1 text-xs rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/60"
                    >
                      {isOpen ? "Réduire" : "Voir tout"}
                    </button>
                    <button
                      onClick={() => copyHTML(m.content)}
                      className="px-2 py-1 text-xs rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/60"
                      title="Copier HTML"
                    >
                      Copier
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function EmptyState({ label = "Aucun élément", hint = "" }) {
  return (
    <div className="py-14 text-center">
      <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
        🗂️
      </div>
      <div className="font-medium">{label}</div>
      {hint ? <div className="text-sm text-slate-500 mt-1">{hint}</div> : null}
    </div>
  );
}
